import { useEffect, useState } from "react";
import { API_BASE } from "../../config/apiConfig";
import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import { useToast } from "../../hooks/useToast";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "../../components/loadingOverlay";

export default function AddAJSSRankingPage() {
  const token = sessionStorage.getItem("access_token");
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();

  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    playerId: "",
    playerName: "",
    ageGroup: "",
    rank: "",
    month: "",
    year: new Date().getFullYear(),
  });

  // ------------------------------------------------------------
  // LOAD PLAYERS
  // ------------------------------------------------------------
  useEffect(() => {
    loadPlayers();
  }, []);

  async function loadPlayers() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance-players`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        setPlayers(data.players);
      }
    } finally {
      setLoading(false);
    }
  }

  // ------------------------------------------------------------
  // FORM HANDLERS
  // ------------------------------------------------------------
  const handlePlayerChange = (e) => {
    const pid = e.target.value;
    const player = players.find((p) => p.id === pid);

    setSelectedPlayer(pid);

    if (player) {
      setForm((prev) => ({
        ...prev,
        playerId: player.id,
        playerName: player.fullName,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ------------------------------------------------------------
  // SUBMIT
  // ------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/performance-ajss-add`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (data.success) {
        showToast("AJSS Ranking added successfully!", "success");
      }
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} />

      <PageHeader
        title="Add AJSS Ranking"
        backTo="/admin-performance-review-landing"
      />

      <form
        onSubmit={handleSubmit}
        className="max-w-2xl mx-auto w-full space-y-4 text-left mb-4"
      >
        {/* Submit + Cancel Buttons */}
        <div className="flex justify-end gap-2">
          <button
            type="submit"
            className="bg-green-600 hover:bg-green-800 p-1 rounded text-white transition"
            title="Submit"
          >
            <FaCheck size={20} />
          </button>

          <button
            type="button"
            onClick={() => navigate("/admin-performance-review-landing")}
            className="p-1 bg-gray-300 rounded"
            title="Cancel"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* PLAYER */}
        <div>
          <label className="block mb-1 font-medium">
            Player <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <select
            name="playerId"
            value={selectedPlayer}
            onChange={handlePlayerChange}
            className="w-full border rounded p-2"
            required
          >
            <option value="">Select Player</option>
            {players.map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </select>
        </div>

        {/* AGE GROUP */}
        <div>
          <label className="block mb-1 font-medium">
            Age Group{" "}
            <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <select
            name="ageGroup"
            value={form.ageGroup}
            onChange={handleChange}
            className="w-full border rounded p-2"
            required
          >
            <option value="">Select Age Group</option>
            {[
              "BU9",
              "GU9",
              "BU11",
              "GU11",
              "BU13",
              "GU13",
              "BU15",
              "GU15",
              "BU17",
              "GU17",
            ].map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>

        {/* RANK */}
        <div>
          <label className="block mb-1 font-medium">
            Exact AJSS Rank{" "}
            <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="number"
            name="rank"
            value={form.rank}
            onChange={handleChange}
            placeholder="Leave blank if not ranked"
            className="w-full border rounded p-2 placeholder:italic placeholder:text-gray-400"
          />
        </div>

        {/* MONTH */}
        <div>
          <label className="block mb-1 font-medium">
            Month{" "}
            <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <select
            name="month"
            value={form.month}
            onChange={handleChange}
            className="w-full border rounded p-2"
            required
          >
            <option value="">Select Month</option>
            {["Feb", "Apr", "Jun", "Aug", "Oct", "Dec"].map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        {/* YEAR */}
        <div>
          <label className="block mb-1 font-medium">
            Year{" "}
            <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <input
            type="number"
            name="year"
            value={form.year}
            onChange={handleChange}
            className="w-full border rounded p-2"
            required
          />
        </div>
      </form>

      <ToastContainer />
    </MainContentLayout>
  );
}
